import {delayedTrigger} from "@cdx/common/delayed-trigger";
import type {ScopeGroup} from "@cdx/shared/auth/scopes";
import {getIdAsKey} from "./mate-shared";
import {isPlaceholderId} from "./optimistic";
import type {
  AddToCacheData,
  CacheVal,
  CollectType,
  InstancePath,
  ModelDesc,
  ModelDescriptions,
  ModelName,
} from "./mate-types";

/** Field names wanted per model path, nested by relation. */
type QueryNode = {fields: Record<string, true>; children: QueryTree};
type QueryTree = Record<string, QueryNode>;

/** The wire format: `{"card(abc)": ["title", {deck: ["title"]}]}` */
type ServerQuery = {[key: string]: (string | ServerQuery)[]};

export type Fetcher = (
  query: ServerQuery,
  sendResults: (error: unknown, result?: unknown) => void
) => void;

type CollectorParams = {
  fetcher: Fetcher;
  addFn: (data: AddToCacheData) => void;
  onNotLoaded: (status: CacheVal["status"]) => void;
  descriptions: ModelDescriptions;
  getMissingReadScope?: (modelName: ModelName, group: ScopeGroup) => string | null;
};

export default function createCollector({
  fetcher,
  addFn,
  onNotLoaded,
  descriptions,
  getMissingReadScope,
}: CollectorParams) {
  let timeoutStarted = false;
  let toBeCollected: QueryTree = {};
  const inflight: Record<number, QueryTree> = {};
  let fetchCounter = 0;
  let ignoreUntilCount = -1;

  let aggrInflight: QueryTree = {};

  function mergeInto(query: QueryTree, dict: QueryTree) {
    Object.keys(query).forEach((modelName) => {
      const entry = query[modelName];
      const alreadyThere = dict[modelName];
      if (!alreadyThere) {
        dict[modelName] = entry;
      } else {
        alreadyThere.fields = {...alreadyThere.fields, ...entry.fields};
        mergeInto(entry.children, alreadyThere.children);
      }
    });
  }

  function updateAggrInflight() {
    aggrInflight = {};

    Object.keys(inflight).forEach((key) => {
      mergeInto(inflight[Number(key)], aggrInflight);
    });
  }

  function fetched(error: unknown, counter: number, result?: unknown) {
    delete inflight[counter];
    updateAggrInflight();
    // the server answers with a map from model name to rows; nothing here can check that
    if (!error && counter > ignoreUntilCount) addFn(result as AddToCacheData);
  }

  function initiateFetch() {
    function addToQuery(data: QueryTree, obj: ServerQuery) {
      Object.keys(data).forEach((key) => {
        const val = data[key];
        obj[key] = Object.keys(val.fields);
        if (Object.keys(val.children).length) {
          obj[key].push(addToQuery(val.children, {}));
        }
      });
      return obj;
    }

    const query = addToQuery(toBeCollected, {});

    const fetchCount = fetchCounter;
    fetchCounter += 1;

    inflight[fetchCount] = toBeCollected;
    updateAggrInflight();
    toBeCollected = {};
    timeoutStarted = false;

    fetcher(query, (error, result) => fetched(error, fetchCount, result));
  }

  function isInflight(path: string[], name: string, type: CollectType): boolean {
    let currentLevel: QueryTree = aggrInflight;
    let node: QueryNode | undefined;
    const notPresent = path.some((entry, index) => {
      node = currentLevel[entry];
      if (!node) return true;
      if (index + 1 < path.length) currentLevel = node.children;
      return false;
    });
    if (notPresent || !node) return false;
    switch (type) {
      case "hasMany":
        return name in node.children;
      case "field":
        return name in node.fields;
      default:
        throw new Error(`unknown type '${type as string}'`);
    }
  }

  /**
   * Walks the path through the model descriptions and throws on a field or relation that doesn't
   * exist, or whose scope group the person doesn't hold. Dev only; the server would answer the
   * same request with a 403.
   */
  function assertReadable(path: InstancePath, name: string, type: CollectType) {
    let modelName: ModelName | null = null;
    for (const pathPart of path) {
      const raw = typeof pathPart === "string" ? pathPart : pathPart.modelName;
      // turn 'undismissedResolvables(project:1)' into 'undismissedResolvables'
      const [relOrModelName] = /^\w+/.exec(raw) ?? [raw];
      if (!modelName) {
        modelName = relOrModelName as ModelName;
      } else {
        const modelDesc: ModelDesc = descriptions[modelName];
        modelName = (modelDesc.hasMany[relOrModelName] || modelDesc.belongsTo[relOrModelName])
          .model;
      }
    }
    if (!modelName) throw new Error(`Can't collect '${name}' without a path`);
    const nameMatch = /^(?:(exists|count):)?(\w+)/.exec(name);
    if (!nameMatch) throw new Error(`Can't parse field name '${name}'`);
    const [, qualifier, pureName] = nameMatch;
    const desc = descriptions[modelName];
    const isRelation = type === "hasMany" || qualifier === "exists" || qualifier === "count";
    const relDesc = desc.hasMany[pureName] || desc.belongsTo[pureName];
    if (isRelation && !relDesc) {
      throw new Error(
        `${modelName} doesn't have ${pureName} relation. I can only offer: ${[
          ...Object.keys(desc.hasMany),
          ...Object.keys(desc.belongsTo),
        ].join(", ")}`
      );
    }
    if (!isRelation && !desc.fields[pureName]) {
      throw new Error(
        `${modelName} doesn't have ${pureName} field. I can only offer: ${Object.keys(
          desc.fields
        ).join(", ")}`
      );
    }
    const {group = "default"} = isRelation ? relDesc : desc.fields[pureName];
    // Mirrors the server: an id prop needs no scope, and a default-group relation is checked
    // on its target model's fields only.
    const needsScope = isRelation ? group !== "default" : !desc.idPropAsArray.includes(pureName);
    // The server lets a person's read of such a key through with a warning until 2026-12-31,
    // then fails the whole batched request with a 403.
    const missingScope = needsScope && getMissingReadScope?.(modelName, group);
    if (missingScope) {
      throw new Error(
        `${modelName}.${pureName} requires ${missingScope}. Check it before rendering the read.`
      );
    }
  }

  /** The instance at the head of the path becomes its query key, e.g. `card(abc)`. */
  function toQueryPath(path: InstancePath): string[] {
    const [head, ...rest] = path;
    if (typeof head !== "object") return path as string[];
    if (head.modelName !== "_root" && (!head.id || head.id === "unknown")) {
      throw new Error(`Trying to collect data with a non-defined-id! ${JSON.stringify(path)}`);
    }
    const key = head.modelName === "_root" ? head.modelName : `${head.modelName}(${head.id})`;
    return [key, ...(rest as string[])];
  }

  let stopCollecting = false;
  const stopTrigger = delayedTrigger();

  return {
    delayCollecting: (ms: number, fn?: () => void) => {
      stopCollecting = true;
      stopTrigger.fire(() => {
        stopCollecting = false;
        if (fn) fn();
      }, ms);
    },
    isCurrentlyLoading: () => timeoutStarted || Object.keys(inflight).length > 0,
    /** Drops every answer still on the wire and asks for the same fields again. */
    clear() {
      ignoreUntilCount = fetchCounter;
      fetchCounter += 1;
      let changed = false;
      Object.entries(inflight).forEach(([key, obj]) => {
        mergeInto(obj, toBeCollected);
        delete inflight[Number(key)];
        changed = true;
      });
      updateAggrInflight();
      if (changed && !timeoutStarted) {
        setTimeout(initiateFetch);
        timeoutStarted = true;
      }
    },
    collect: (
      path: InstancePath,
      name: string,
      type: CollectType,
      currStatus?: CacheVal["status"]
    ) => {
      onNotLoaded(currStatus || "loading");
      if (stopCollecting) return;
      if (path.some((p) => typeof p === "object" && isPlaceholderId(getIdAsKey(p.id)))) {
        console.warn(path, "names a row the server doesn't know yet");
        return;
      }
      if (process.env.NODE_ENV !== "production") assertReadable(path, name, type);
      const queryPath = toQueryPath(path);

      if (isInflight(queryPath, name, type)) return;

      let currentLevel = toBeCollected;
      let node: QueryNode | undefined;
      queryPath.forEach((entry, index) => {
        node = currentLevel[entry] = currentLevel[entry] || {fields: {}, children: {}};
        if (index + 1 < queryPath.length) currentLevel = node.children;
      });
      if (!node) throw new Error(`Can't collect '${name}' without a path`);

      switch (type) {
        case "hasMany":
          node.children[name] = node.children[name] || {fields: {}, children: {}};
          break;
        case "field":
          node.fields[name] = true;
          break;
        default:
          throw new Error(`unknown type '${type as string}'`);
      }

      if (!timeoutStarted) {
        setTimeout(initiateFetch);
        timeoutStarted = true;
      }
    },
  };
}
